import { useState } from "react";
import { useNavigate } from "react-router";
import { Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableHeader, TableRow } from "@/components/ui/table";
import {
  PageActions,
  PageTitle,
} from "@/components/atomic-crm/layout/PageActions";
import { NewCompanyDialog } from "@/modules/platform/NewCompanyDialog";
import { PlatformGuard } from "@/modules/platform/PlatformGuard";
import {
  usePlatformCompanies,
  type PlatformCompany,
} from "@/modules/platform/platformApi";
import {
  GridCells,
  GridRowEnd,
  GridHeaderRow,
  useDataGrid,
  useGridClientRows,
  type GridColumn,
} from "@/modules/shared/dataGrid";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const COMPANY_COLUMNS: GridColumn<PlatformCompany>[] = [
  {
    id: "name",
    label: "Company",
    type: "text",
    sortable: true,
    filterable: true,
    alwaysVisible: true,
    cell: (company) => (
      <>
        {company.name}
        {company.is_home ? (
          <Badge variant="secondary" className="ml-2">
            Home
          </Badge>
        ) : null}
      </>
    ),
  },
  {
    id: "admin",
    label: "Admin",
    type: "text",
    sortable: true,
    filterable: true,
    value: (company) => company.admin_emails[0] ?? "",
    cellClassName: "text-muted-foreground",
    cell: (company) => company.admin_emails[0] ?? "—",
  },
  {
    id: "member_count",
    label: "Users",
    type: "number",
    sortable: true,
    filterable: true,
    align: "right",
    cellClassName: "tabular-nums",
    cell: (company) => company.member_count,
  },
  {
    id: "created_at",
    label: "Created",
    type: "date",
    sortable: true,
    filterable: true,
    cellClassName: "text-muted-foreground",
    cell: (company) => formatDate(company.created_at),
  },
  {
    id: "status",
    label: "Status",
    type: "select",
    sortable: true,
    filterable: true,
    options: [
      { value: "active", label: "Active" },
      { value: "disabled", label: "Disabled" },
    ],
    value: (company) => (company.disabled_at ? "disabled" : "active"),
    cell: (company) =>
      company.disabled_at ? (
        <Badge variant="muted">Disabled</Badge>
      ) : (
        <Badge variant="success">Active</Badge>
      ),
  },
];

/** Every company on the platform (operators only). */
export const PlatformCompaniesPage = () => (
  <PlatformGuard>
    <CompaniesList />
  </PlatformGuard>
);

const CompaniesList = () => {
  const navigate = useNavigate();
  const { data: companies, isPending, error } = usePlatformCompanies();
  const [creating, setCreating] = useState(false);
  const grid = useDataGrid({
    tableId: "platform.companies",
    columns: COMPANY_COLUMNS,
  });
  const { rows, sort, onSort } = useGridClientRows(
    companies ?? [],
    COMPANY_COLUMNS,
    grid.filters,
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <PageActions>
        <PageTitle label="Platform · Companies" />
        <Button type="button" size="sm" onClick={() => setCreating(true)}>
          <Plus className="size-4" />
          New company
        </Button>
      </PageActions>

      <div className="flex flex-wrap items-center gap-2">
        {grid.filterButton}
        {grid.columnsButton}
        {grid.chips}
      </div>

      {error ? (
        <p className="text-sm text-destructive">{error.message}</p>
      ) : isPending ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <GridHeaderRow
                grid={grid.header}
                columns={grid.visible}
                sort={sort}
                onSort={onSort}
              />
            </TableHeader>
            <TableBody>
              {rows.map((company) => (
                <TableRow
                  key={company.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/platform/companies/${company.id}`)}
                >
                  <GridCells columns={grid.visible} row={company} />
                  <GridRowEnd />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewCompanyDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
};
